import CustomAutoComplete, {
  Option,
} from "@/modules/common/components/input/dropdowns/auto-complete";
import ConfirmationModal from "@/modules/common/components/modals/cofirmation-modal";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";
import { useStatusesByTrackStage } from "@/modules/property-tracks/hooks/property-stage/statuses-by-track-stage.hook";
import { useUpdateTrackStatus } from "@/modules/property-tracks/hooks/property-stage/update-track-status.hook";
import { Status } from "@/modules/property-tracks/types";
import { useEffect, useState } from "react";
import { queryClient } from "@/queryClient";
import StatusPropertyFields from "./status-property-fields";
import useStatusPropertyFields from "@/modules/property-tracks/hooks/property-stage/status-property-fields.hook";
import { useTrackQuery } from "@/modules/property-tracks/hooks/track.hook";
import { useGetDrawingRequests } from "@/modules/property-tracks/hooks/drawing-requests/use-drawing-requests.hook";

export default function UpdateStatusDropdown({
  trackUId,
  trackStageId,
  defaultStatus,
  trackTypeId,
}: Readonly<{
  trackUId?: string;
  trackStageId?: number;
  defaultStatus?: Status;
  trackTypeId?: number;
}>) {
  const { data: track } = useTrackQuery({ uid: trackUId });
  const rentalTag = track?.property?.propertyFinishing?.rentalTag;
  const { data, isLoading } = useStatusesByTrackStage(trackStageId, rentalTag);

  const [disabled, setDisabled] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [status, setStatus] = useState({
    label: defaultStatus?.name,
    value: defaultStatus?.id.toString(),
    color: "#000",
  } as Option);
  const [selectedStatus, setSelectedStatus] = useState(status);
  const [showPropertyFields, setShowPropertyFields] = useState(false);

  const { fields: mandatedFields, isLoading: isLoadingMandatedFields } =
    useStatusPropertyFields({
      statusId:
        selectedStatus && selectedStatus.value
          ? Number(selectedStatus.value)
          : undefined,
    });

  const propertyUid = track?.property.uid ?? "";

  const { data: drawingRequests } = useGetDrawingRequests({ propertyUid });

  useEffect(() => {
    if (!selectedStatus) return;
    if (selectedStatus.value === defaultStatus?.id.toString()) return;
    if (isLoadingMandatedFields) return;

    const isContractSignedStatus =
      selectedStatus?.label?.toLowerCase() === "contract signed";

    if (
      isContractSignedStatus &&
      drawingRequests &&
      drawingRequests.length > 0 &&
      !drawingRequests.some((r) => r.isApprovedForExecution)
    ) {
      showToast({
        type: ToastType.ERROR,
        message:
          "Please mark a drawing proposal as final before changing the track status.",
        toastId: TOAST_IDS.UPDATE_TRACK_STATUS_ERROR,
        options: { position: "bottom-left", autoClose: 5000 },
      });
      resetDefaultStatus();
      return;
    }

    if (mandatedFields && mandatedFields.length > 0) {
      setShowPropertyFields(true);
    } else {
      setConfirmModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, isLoadingMandatedFields]);

  const mutationUpdateTrackStatus = useUpdateTrackStatus();

  const onDropdownValueChange = (data: Option) => {
    setStatus(data);
    setSelectedStatus(data);
  };

  const onSubmitUpdateTrackStatus = () => {
    setConfirmModalOpen(false);
    setDisabled(true);
    setSelectedStatus(status);
    submitUpdateTrackStatus(status);
  };

  const resetDefaultStatus = () => {
    setSelectedStatus({
      label: defaultStatus?.name,
      value: defaultStatus?.id.toString(),
    } as Option);
  };

  const submitUpdateTrackStatus = (data: Option) => {
    mutationUpdateTrackStatus.mutate(
      {
        uid: trackUId ?? "",
        statusId: Number(data.value),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["track-next-actions"],
            refetchType: "all",
          });

          setDisabled(false);
          showToast({
            type: ToastType.SUCCESS,
            message: "Track Status updated successfully",
            toastId: TOAST_IDS.UPDATE_TRACK_STATUS_SUCCESS,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
        onError: (error: { response?: { data?: { message?: string } } }) => {
          setDisabled(false);
          setSelectedStatus({
            label: defaultStatus?.name,
            value: defaultStatus?.id.toString(),
          } as Option);
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.UPDATE_TRACK_STATUS_ERROR,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
      },
    );
  };

  return (
    <>
      <CustomAutoComplete
        disabled={disabled}
        popperMinWidth="250px"
        options={
          data?.map((status) => ({
            label: status.name,
            value: status.id.toString(),
            color: status.color,
          })) || []
        }
        customPlaceholderSx={{
          "& .MuiInputBase-input": {
            textAlign: "center",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            paddingRight: "28px !important",
            fontSize: "13px",
            fontFamily: "inherit",
          },
        }}
        loading={isLoading}
        value={selectedStatus}
        placeholder="Choose a Status"
        disableClearable={true}
        onChange={(value) => {
          onDropdownValueChange(value as Option);
        }}
      />

      <ConfirmationModal
        open={confirmModalOpen}
        setOpen={setConfirmModalOpen}
        title="Change Track Status"
        body={`Are you sure you want to change track status to ${status.label} ?`}
        onConfirmHandler={onSubmitUpdateTrackStatus}
      />

      {showPropertyFields && selectedStatus && (
        <StatusPropertyFields
          trackUId={trackUId ?? ""}
          statusId={Number(selectedStatus.value)}
          open={showPropertyFields}
          setOpen={setShowPropertyFields}
          resetDefaultStatus={resetDefaultStatus}
          trackTypeId={trackTypeId}
        />
      )}
    </>
  );
}
